import { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { streamChat, type StreamEvent } from '../api/streamChat';
import {
  AlertTriangle,
  Bot,
  Send,
  Mic,
  MicOff,
  Loader2,
  Sparkles,
  Trash2,
  Plus,
  MessageSquare,
  Paperclip,
  X as XIcon,
  Image as ImageIcon,
  FileText,
  FileSpreadsheet,
  File as FileIcon,
} from 'lucide-react';
import { VoiceSettings } from '../components/VoiceSettings';
import { ChartRenderer } from '../components/ChartRenderer';
import { ActionPreviewCard } from '../components/ActionPreviewCard';
import { SourcesCard } from '../components/SourcesCard';
import type { RagSource } from '../api/streamChat';
import { useQueryClient } from '@tanstack/react-query';
import {
  useConversations,
  useConversation,
  useSendMessage,
  useDeleteConversation,
  attachmentUrl,
  aiKeys,
} from '../api/assistant';
import { useSpeechToText } from '../hooks/useSpeechToText';
import { useVoiceOutput } from '../hooks/useVoiceOutput';
import type { ChatAttachment, ChatMessage, AttachmentKind } from '../types/chat';

const MAX_FILES = 5;
const MAX_BYTES = 10 * 1024 * 1024; // 10 MB
const ACCEPTED_MIME = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv',
  'text/markdown',
];
const ACCEPT_ATTR = '.jpg,.jpeg,.png,.webp,.gif,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.md';

function classifyFile(file: File): AttachmentKind {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type === 'application/pdf') return 'pdf';
  if (file.type.includes('word') || file.name.match(/\.docx?$/i)) return 'word';
  if (file.type.includes('sheet') || file.type.includes('excel') || file.name.match(/\.xlsx?$/i)) return 'excel';
  if (file.type.startsWith('text/') || file.name.match(/\.(txt|csv|md)$/i)) return 'text';
  return 'other';
}

function AttachmentIcon({ kind, size = 16 }: { kind: AttachmentKind; size?: number }) {
  switch (kind) {
    case 'image': return <ImageIcon size={size} />;
    case 'excel': return <FileSpreadsheet size={size} />;
    case 'pdf':
    case 'word':
    case 'text':  return <FileText size={size} />;
    default:      return <FileIcon size={size} />;
  }
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

const SUGGESTIONS = [
  '¿Cuántos clientes tenemos?',
  'Top 5 facturas más altas del año',
  '¿Qué empleados tienen más vacaciones pendientes?',
  'Resume las incidencias IT abiertas',
  '¿Cuánto facturamos el mes pasado?',
];

type StreamingState = {
  stage: string | null;
  liveText: string;
  isStreaming: boolean;
  chartSpec: Record<string, unknown> | null;
  sources: RagSource[];
};

const STAGE_LABELS: Record<string, string> = {
  analyzing_attachments: 'Analizando adjuntos…',
  searching_documents:   'Buscando documentos relevantes…',
  generating_sql:        'Generando consulta SQL…',
  executing_sql:         'Consultando base de datos…',
  summarizing:           'Redactando respuesta…',
  generating_action:     'Preparando acción…',
};

export function AssistantPage() {
  const [convId, setConvId] = useState<number | null>(null);
  const [input, setInput] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [streaming, setStreaming] = useState<StreamingState>({
    stage: null,
    liveText: '',
    isStreaming: false,
    chartSpec: null,
    sources: [],
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const qc = useQueryClient();
  const { data: conversations } = useConversations();
  const { data: convData } = useConversation(convId);
  const sendMutation = useSendMessage();         // se mantiene por compatibilidad pero no la usamos para enviar
  const deleteMutation = useDeleteConversation();
  void sendMutation;

  const voiceIn = useSpeechToText();
  const voiceOut = useVoiceOutput();

  // Cuando llega texto final del micro, lo metemos en el input
  useEffect(() => {
    if (voiceIn.finalTranscript) {
      setInput(voiceIn.finalTranscript);
    }
  }, [voiceIn.finalTranscript]);

  // Auto-scroll al último mensaje
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [convData?.messages.length, streaming.isStreaming, streaming.liveText.length]);

  // Hablar la última respuesta del asistente
  useEffect(() => {
    if (!sendMutation.data) return;
    voiceOut.speak(sendMutation.data.content);
  }, [sendMutation.data]);

  const messages: ChatMessage[] = convData?.messages ?? [];

  const handleSend = async (text: string) => {
    const trimmed = text.trim();
    if ((!trimmed && pendingFiles.length === 0) || streaming.isStreaming) return;

    setErrorBanner(null);
    setStreaming({ stage: 'starting', liveText: '', isStreaming: true, chartSpec: null, sources: [] });

    const filesToSend = [...pendingFiles];
    setInput('');
    setPendingFiles([]);
    voiceIn.reset();

    const controller = new AbortController();
    abortRef.current = controller;

    let finalText = '';

    try {
      await streamChat(
        {
          message: trimmed || '(adjunto sin texto)',
          conversation_id: convId,
          files: filesToSend.length > 0 ? filesToSend : undefined,
        },
        (ev: StreamEvent) => {
          switch (ev.type) {
            case 'conversation':
              if (!convId) {
                setConvId(ev.id);
              }
              break;
            case 'stage':
              setStreaming((s) => ({ ...s, stage: ev.stage }));
              break;
            case 'token':
              finalText += ev.text;
              setStreaming((s) => ({ ...s, liveText: s.liveText + ev.text }));
              break;
            case 'chart':
              setStreaming((s) => ({ ...s, chartSpec: (ev as any).spec as Record<string, unknown> }));
              break;
            case 'sources':
              setStreaming((s) => ({ ...s, sources: (ev as any).items as RagSource[] }));
              break;
            case 'done':
              setStreaming({ stage: null, liveText: '', isStreaming: false, chartSpec: null, sources: [] });
              // Refrescar la conversación para que cargue el mensaje persistido
              qcInvalidate();
              if (finalText.trim()) voiceOut.speak(finalText);
              break;
            case 'error':
              setErrorBanner(ev.message);
              setStreaming({ stage: null, liveText: '', isStreaming: false, chartSpec: null, sources: [] });
              break;
          }
        },
        controller.signal,
      );
    } catch (e: any) {
      if (e?.name !== 'AbortError') {
        setErrorBanner(e?.message ?? 'Error de conexión al asistente.');
      }
      setStreaming({ stage: null, liveText: '', isStreaming: false, chartSpec: null, sources: [] });
    }
  };

  // Helper para refrescar la lista de conversaciones y la actual tras stream
  const qcInvalidate = () => {
    qc.invalidateQueries({ queryKey: aiKeys.conversations() });
    if (convId) {
      qc.invalidateQueries({ queryKey: aiKeys.conversation(convId) });
    }
  };

  const cancelStreaming = () => {
    abortRef.current?.abort();
    setStreaming({ stage: null, liveText: '', isStreaming: false, chartSpec: null, sources: [] });
  };

  const handleVoiceToggle = () => {
    if (voiceIn.isListening) {
      voiceIn.stop();
    } else {
      voiceIn.start();
    }
  };

  const newConversation = () => {
    setConvId(null);
    setInput('');
    setPendingFiles([]);
    voiceIn.reset();
    voiceOut.cancel();
  };

  /* ───────────────── Adjuntos ───────────────── */

  const addFiles = (incoming: FileList | File[]) => {
    const arr = Array.from(incoming);
    const accepted: File[] = [];
    const rejected: string[] = [];

    for (const f of arr) {
      if (pendingFiles.length + accepted.length >= MAX_FILES) {
        rejected.push(`${f.name}: máx ${MAX_FILES} archivos por mensaje`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        rejected.push(`${f.name}: supera ${MAX_BYTES / 1024 / 1024} MB`);
        continue;
      }
      const okType = ACCEPTED_MIME.includes(f.type) || ACCEPT_ATTR.split(',').some((ext) => f.name.toLowerCase().endsWith(ext.replace('.', '.')));
      if (!okType) {
        rejected.push(`${f.name}: tipo no soportado`);
        continue;
      }
      accepted.push(f);
    }

    if (accepted.length > 0) {
      setPendingFiles((prev) => [...prev, ...accepted]);
    }
    if (rejected.length > 0) {
      setErrorBanner(rejected.join(' · '));
    }
  };

  const removeFile = (idx: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(e.target.files);
    e.target.value = ''; // permite re-elegir el mismo archivo
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.types.includes('Files')) setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) addFiles(e.dataTransfer.files);
  };

  const handleDelete = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('¿Eliminar esta conversación?')) return;
    deleteMutation.mutate(id);
    if (id === convId) newConversation();
  };

  return (
    <div className="flex h-full">
      {/* Sidebar de conversaciones */}
      <aside className="w-72 bg-white border-r border-slate-200 flex flex-col">
        <div className="p-4 border-b border-slate-200">
          <button
            onClick={newConversation}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-sm"
          >
            <Plus size={16} /> Nueva conversación
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {conversations?.length === 0 && (
            <div className="text-center text-slate-400 text-sm py-8 px-4">
              Aún no hay conversaciones. Empieza preguntando algo.
            </div>
          )}
          {conversations?.map((c) => (
            <button
              key={c.id}
              onClick={() => setConvId(c.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 group transition ${
                c.id === convId
                  ? 'bg-emerald-50 text-emerald-800 font-bold'
                  : 'hover:bg-slate-100 text-slate-700'
              }`}
            >
              <MessageSquare size={14} className="flex-shrink-0 opacity-60" />
              <span className="flex-1 truncate">{c.title || 'Sin título'}</span>
              <Trash2
                size={14}
                onClick={(e) => handleDelete(c.id, e)}
                className="opacity-0 group-hover:opacity-100 hover:text-red-600 transition"
              />
            </button>
          ))}
        </div>
      </aside>

      {/* Main chat */}
      <main
        className="relative flex-1 flex flex-col bg-gradient-to-br from-slate-50 to-white"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isDragging && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-emerald-50/90 border-4 border-dashed border-emerald-400 pointer-events-none">
            <div className="text-center">
              <Paperclip size={48} className="mx-auto text-emerald-600 mb-2" />
              <p className="text-lg font-black text-emerald-800">Suelta tus archivos aquí</p>
              <p className="text-xs text-emerald-700 mt-1">
                Imágenes, PDF, Word, Excel, CSV o TXT (máx {MAX_FILES} × {MAX_BYTES / 1024 / 1024}MB)
              </p>
            </div>
          </div>
        )}
        {/* Header */}
        <header className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-emerald-700 to-emerald-500 flex items-center justify-center text-white">
              <Bot size={20} />
            </div>
            <div>
              <div className="font-black text-lg">Intraeco IA</div>
              <div className="text-xs text-slate-500">
                Pregunta lo que quieras sobre tus datos · qwen2.5-coder local
              </div>
            </div>
          </div>

          <VoiceSettings voice={voiceOut} />
        </header>

        {errorBanner && (
          <div className="mx-6 mt-4 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <AlertTriangle size={18} className="mt-0.5 flex-shrink-0" />
            <div className="flex-1 whitespace-pre-wrap">{errorBanner}</div>
            <button
              onClick={() => setErrorBanner(null)}
              className="text-red-600 hover:text-red-900 text-xs font-bold"
            >
              ✕
            </button>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.length === 0 && !streaming.isStreaming && (
            <WelcomeScreen onSuggestion={handleSend} />
          )}

          {messages.map((m) => (
            <MessageBubble key={m.id} message={m} />
          ))}

          {streaming.isStreaming && (
            <StreamingBubble
              stage={streaming.stage}
              liveText={streaming.liveText}
              chartSpec={streaming.chartSpec}
              sources={streaming.sources}
            />
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-slate-200 bg-white">
          <div className="max-w-3xl mx-auto">
            {/* Preview de archivos pendientes de enviar (estilo ChatGPT) */}
            {pendingFiles.length > 0 && (
              <div className="mb-3 flex flex-wrap gap-2">
                {pendingFiles.map((f, i) => {
                  const kind = classifyFile(f);
                  if (kind === 'image') {
                    return (
                      <div
                        key={`${f.name}-${i}`}
                        className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
                      >
                        <img
                          src={URL.createObjectURL(f)}
                          alt={f.name}
                          className="h-16 w-16 object-cover"
                          onLoad={(e) => URL.revokeObjectURL((e.target as HTMLImageElement).src)}
                        />
                        <button
                          onClick={() => removeFile(i)}
                          className="absolute top-1 right-1 rounded-full bg-slate-900/70 p-0.5 text-white opacity-0 group-hover:opacity-100 hover:bg-red-600 transition"
                          title="Quitar"
                        >
                          <XIcon size={12} />
                        </button>
                      </div>
                    );
                  }
                  return (
                    <div
                      key={`${f.name}-${i}`}
                      className="group relative flex items-center gap-2.5 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm"
                    >
                      <div className={`grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg ${kindBgColor(kind)}`}>
                        <AttachmentIcon kind={kind} size={16} />
                      </div>
                      <div className="max-w-[180px]">
                        <div className="truncate text-xs font-bold text-slate-700">{f.name}</div>
                        <div className="text-[10px] text-slate-500">
                          {kindLabel(kind)} · {formatBytes(f.size)}
                        </div>
                      </div>
                      <button
                        onClick={() => removeFile(i)}
                        className="rounded-full p-1 text-slate-400 hover:bg-red-50 hover:text-red-600 transition"
                        title="Quitar"
                      >
                        <XIcon size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {voiceIn.isListening && (
              <div className="text-xs text-emerald-700 font-bold mb-2 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                {voiceIn.engine === 'google' ? 'Escuchando…' : 'Grabando…'}{' '}
                <span className="font-normal text-slate-600">{voiceIn.transcript}</span>
              </div>
            )}

            {voiceIn.isTranscribing && (
              <div className="text-xs text-emerald-700 font-bold mb-2 flex items-center gap-2">
                <Loader2 size={12} className="animate-spin" />
                {voiceIn.modelLoadProgress > 0 && voiceIn.modelLoadProgress < 100 ? (
                  <>Descargando modelo Whisper… {Math.round(voiceIn.modelLoadProgress)}%</>
                ) : (
                  <>Transcribiendo…</>
                )}
              </div>
            )}

            {voiceIn.error && !voiceIn.isListening && !voiceIn.isTranscribing && (
              <div className="mb-2 flex items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
                <span className="flex items-center gap-2">
                  <AlertTriangle size={12} />
                  {voiceIn.error}
                </span>
                <button
                  onClick={voiceIn.reset}
                  className="text-amber-700 hover:text-amber-900 font-bold"
                >
                  ✕
                </button>
              </div>
            )}

            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend(input);
                  }
                }}
                placeholder="Pregunta lo que quieras o arrastra un archivo... (Enter para enviar)"
                rows={2}
                className="flex-1 resize-none rounded-2xl border border-slate-300 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-200 px-4 py-3 outline-none text-sm"
              />

              {/* Botón adjuntar */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPT_ATTR}
                onChange={handleFileInputChange}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={streaming.isStreaming || pendingFiles.length >= MAX_FILES}
                className="p-3 rounded-2xl border bg-white border-slate-300 text-slate-600 hover:border-emerald-400 hover:text-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition relative"
                title={
                  pendingFiles.length >= MAX_FILES
                    ? `Máximo ${MAX_FILES} archivos por mensaje`
                    : 'Adjuntar imagen, PDF, Word, Excel...'
                }
              >
                <Paperclip size={20} />
                {pendingFiles.length > 0 && (
                  <span className="absolute -top-1 -right-1 grid h-5 min-w-5 place-items-center rounded-full bg-emerald-600 px-1 text-[10px] font-black text-white">
                    {pendingFiles.length}
                  </span>
                )}
              </button>

              {voiceIn.supported && (
                <button
                  onClick={handleVoiceToggle}
                  disabled={streaming.isStreaming || voiceIn.isTranscribing}
                  className={`p-3 rounded-2xl border transition ${
                    voiceIn.isListening
                      ? 'bg-red-500 border-red-600 text-white animate-pulse'
                      : voiceIn.isTranscribing
                        ? 'bg-emerald-100 border-emerald-300 text-emerald-700'
                        : 'bg-white border-slate-300 text-slate-600 hover:border-emerald-400 hover:text-emerald-700'
                  }`}
                  title={
                    voiceIn.isListening
                      ? 'Parar de grabar'
                      : voiceIn.isTranscribing
                        ? 'Transcribiendo…'
                        : 'Hablar'
                  }
                >
                  {voiceIn.isTranscribing ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : voiceIn.isListening ? (
                    <MicOff size={20} />
                  ) : (
                    <Mic size={20} />
                  )}
                </button>
              )}

              {streaming.isStreaming ? (
                <button
                  onClick={cancelStreaming}
                  className="p-3 rounded-2xl bg-red-600 hover:bg-red-700 text-white"
                  title="Cancelar"
                >
                  <XIcon size={20} />
                </button>
              ) : (
                <button
                  onClick={() => handleSend(input)}
                  disabled={!input.trim() && pendingFiles.length === 0}
                  className="p-3 rounded-2xl bg-emerald-700 hover:bg-emerald-800 disabled:opacity-40 disabled:cursor-not-allowed text-white"
                >
                  <Send size={20} />
                </button>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user';
  const hasAttachments = (message.attachments?.length ?? 0) > 0;
  const cleanContent = message.content?.trim() ?? '';
  // El frontend manda "(adjunto sin texto)" cuando solo hay archivos: no lo mostramos en burbuja
  const hasRealContent =
    cleanContent.length > 0 && cleanContent !== '(adjunto sin texto)';

  /* ─────────── Usuario con adjuntos: estilo ChatGPT (separados) ─────────── */
  if (isUser && hasAttachments) {
    return (
      <div className="flex gap-3 max-w-3xl ml-auto flex-row-reverse">
        <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 bg-slate-200 text-slate-700">
          👤
        </div>
        <div className="flex flex-col items-end gap-2 min-w-0">
          {/* Adjuntos arriba, cada uno en su propia card */}
          <div className="flex flex-col items-end gap-2">
            {message.attachments!.map((a) => (
              <UserAttachmentCard key={a.id} attachment={a} />
            ))}
          </div>
          {/* Texto debajo, solo si lo hay */}
          {hasRealContent && (
            <div className="rounded-2xl px-4 py-3 shadow-sm bg-emerald-700 text-white whitespace-pre-wrap text-sm max-w-3xl">
              {message.content}
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ─────────── Resto: burbuja única (asistente o usuario sin adjuntos) ─────────── */
  return (
    <div className={`flex gap-3 max-w-3xl ${isUser ? 'ml-auto flex-row-reverse' : ''}`}>
      {isUser ? (
        <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 bg-slate-200 text-slate-700">
          👤
        </div>
      ) : (
        <AssistantAvatar size={44} />
      )}
      <div
        className={`rounded-2xl px-4 py-3 shadow-sm text-sm ${
          isUser
            ? 'bg-emerald-700 text-white whitespace-pre-wrap'
            : 'bg-white border border-slate-200'
        }`}
      >
        {isUser ? (
          message.content
        ) : (
          <MarkdownContent text={message.content} />
        )}

        {/* Adjuntos del asistente (raro, pero por si acaso) */}
        {!isUser && hasAttachments && (
          <div className="mt-3 flex flex-wrap gap-2">
            {message.attachments!.map((a) => (
              <AttachmentBubble key={a.id} attachment={a} isUserBubble={false} />
            ))}
          </div>
        )}

        {!isUser && message.chart_spec && (
          <ChartRenderer spec={message.chart_spec} />
        )}

        {!isUser && message.action && (
          <ActionPreviewCard action={message.action} />
        )}

        {message.sql_query && (
          <details className="mt-3 pt-3 border-t border-slate-200/60 text-xs">
            <summary className="cursor-pointer text-slate-500 hover:text-slate-700 font-bold">
              Ver consulta SQL ejecutada
            </summary>
            <pre className="mt-2 bg-slate-900 text-slate-100 rounded-lg p-3 overflow-x-auto">
              <code>{message.sql_query}</code>
            </pre>
            {message.sql_result && message.sql_result.length > 0 && (
              <div className="mt-2">
                <div className="text-slate-500 mb-1 font-bold">
                  Resultado ({message.sql_result.length} filas):
                </div>
                <pre className="bg-slate-50 border border-slate-200 rounded-lg p-2 overflow-x-auto max-h-48">
                  <code>{JSON.stringify(message.sql_result, null, 2)}</code>
                </pre>
              </div>
            )}
          </details>
        )}

        {message.duration_ms !== null && message.duration_ms !== undefined && !isUser && (
          <div className="text-[10px] text-slate-400 mt-2">
            {(message.duration_ms / 1000).toFixed(1)}s
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Card de adjunto en mensajes del usuario, estilo ChatGPT/Claude.
 * - Imágenes: thumbnail grande con esquinas redondeadas
 * - Documentos: tarjeta horizontal compacta con icono + nombre + tipo + tamaño
 */
function UserAttachmentCard({ attachment }: { attachment: ChatAttachment }) {
  const url = attachmentUrl(attachment.id);
  const isImage = attachment.kind === 'image';

  if (isImage) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="block max-w-xs overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition"
        title={attachment.original_name}
      >
        <img
          src={url}
          alt={attachment.original_name}
          className="w-full h-auto object-cover max-h-64"
        />
      </a>
    );
  }

  // Tarjeta horizontal estilo ChatGPT para documentos
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm hover:shadow-md hover:border-emerald-300 transition min-w-[240px] max-w-xs"
    >
      <div className={`grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl ${kindBgColor(attachment.kind)}`}>
        <AttachmentIcon kind={attachment.kind} size={20} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-bold text-slate-800">{attachment.original_name}</div>
        <div className="text-[11px] text-slate-500">
          {kindLabel(attachment.kind)} · {formatBytes(attachment.size_bytes)}
        </div>
      </div>
    </a>
  );
}

function kindBgColor(kind: AttachmentKind): string {
  switch (kind) {
    case 'pdf':   return 'bg-red-50 text-red-600 border border-red-100';
    case 'word':  return 'bg-blue-50 text-blue-600 border border-blue-100';
    case 'excel': return 'bg-emerald-50 text-emerald-600 border border-emerald-100';
    case 'text':  return 'bg-slate-50 text-slate-600 border border-slate-200';
    case 'image': return 'bg-purple-50 text-purple-600 border border-purple-100';
    default:      return 'bg-slate-50 text-slate-500 border border-slate-200';
  }
}

function kindLabel(kind: AttachmentKind): string {
  switch (kind) {
    case 'pdf':   return 'PDF';
    case 'word':  return 'Word';
    case 'excel': return 'Excel';
    case 'text':  return 'Texto';
    case 'image': return 'Imagen';
    default:      return 'Archivo';
  }
}

function AssistantAvatar({ size = 44 }: { size?: number }) {
  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 flex items-center justify-center overflow-hidden flex-shrink-0 shadow-sm"
      style={{ width: size, height: size }}
    >
      <img
        src="/assistant-mascot.png"
        alt="IA"
        className="w-full h-full object-contain scale-125"
        onError={(e) => {
          // Fallback al icono Bot si la imagen no carga
          const img = e.target as HTMLImageElement;
          img.style.display = 'none';
          const parent = img.parentElement;
          if (parent && !parent.querySelector('.fallback-bot')) {
            parent.classList.add('bg-gradient-to-br', 'from-emerald-700', 'to-emerald-500');
            parent.innerHTML = '<svg class="fallback-bot" width="60%" height="60%" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="10" x="3" y="11" rx="2"/><circle cx="12" cy="5" r="2"/><path d="M12 7v4"/><line x1="8" x2="8" y1="16" y2="16"/><line x1="16" x2="16" y1="16" y2="16"/></svg>';
          }
        }}
      />
    </div>
  );
}

function WelcomeScreen({ onSuggestion }: { onSuggestion: (text: string) => void }) {
  return (
    <div className="flex h-full min-h-[60vh] flex-col items-center justify-center px-6 text-center">
      {/* Mascota animada */}
      <div className="relative mb-6">
        <div className="absolute inset-0 -z-10 mx-auto h-48 w-48 rounded-full bg-emerald-200/40 blur-3xl" />
        <img
          src="/assistant-mascot.png"
          alt="Asistente Intraeco"
          className="mx-auto h-56 w-auto drop-shadow-xl animate-float"
          onError={(e) => {
            // Fallback si no se encuentra la imagen
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      </div>

      {/* Saludo */}
      <h1 className="text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
        Buenas,{' '}
        <span className="bg-gradient-to-r from-emerald-700 to-emerald-500 bg-clip-text text-transparent">
          ¿en qué puedo ayudarte?
        </span>
      </h1>
      <p className="mt-3 max-w-xl text-sm font-semibold text-slate-500 sm:text-base">
        Pregúntame en lenguaje natural sobre cualquier dato del ERP. Yo lo consulto,
        analizo y te respondo. Puedes adjuntar documentos o imágenes.
      </p>

      {/* Sugerencias */}
      <div className="mt-8 grid w-full max-w-2xl gap-2 sm:grid-cols-2">
        {SUGGESTIONS.map((s) => (
          <button
            key={s}
            onClick={() => onSuggestion(s)}
            className="group flex items-center gap-2 rounded-xl border border-slate-200 bg-white/80 px-4 py-3 text-left text-sm font-semibold text-slate-700 transition hover:-translate-y-0.5 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800 hover:shadow-soft"
          >
            <Sparkles size={14} className="flex-shrink-0 text-emerald-500 transition group-hover:text-emerald-600" />
            <span className="flex-1">{s}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function MarkdownContent({ text }: { text: string }) {
  return (
    <div className="prose prose-sm max-w-none prose-slate prose-p:my-1.5 prose-headings:mb-1.5 prose-headings:mt-3 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0 prose-code:bg-slate-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-emerald-700 prose-code:before:content-none prose-code:after:content-none prose-table:my-2 prose-th:bg-slate-100 prose-th:px-2 prose-th:py-1 prose-td:px-2 prose-td:py-1 prose-th:border prose-td:border prose-th:border-slate-200 prose-td:border-slate-200">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
    </div>
  );
}

function StreamingBubble({
  stage,
  liveText,
  chartSpec,
  sources,
}: {
  stage: string | null;
  liveText: string;
  chartSpec: Record<string, unknown> | null;
  sources: RagSource[];
}) {
  const stageLabel = stage ? STAGE_LABELS[stage] ?? 'Procesando…' : 'Iniciando…';
  return (
    <div className="flex gap-3 max-w-3xl">
      <AssistantAvatar size={44} />
      <div className="flex-1 bg-white border border-slate-200 rounded-2xl px-4 py-3 shadow-sm">
        {liveText ? (
          <>
            <MarkdownContent text={liveText} />
            <span className="inline-block w-1.5 h-4 bg-emerald-600 animate-pulse ml-0.5 align-middle" />
          </>
        ) : (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="animate-spin" size={16} />
            <span>{stageLabel}</span>
          </div>
        )}
        {sources.length > 0 && <SourcesCard sources={sources} />}
        {chartSpec && <ChartRenderer spec={chartSpec} />}
      </div>
    </div>
  );
}

function AttachmentBubble({
  attachment,
  isUserBubble,
}: {
  attachment: ChatAttachment;
  isUserBubble: boolean;
}) {
  const url = attachmentUrl(attachment.id);
  const isImage = attachment.kind === 'image';

  if (isImage) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="block max-w-[200px] overflow-hidden rounded-xl border border-white/20 hover:opacity-90 transition"
        title={attachment.original_name}
      >
        <img
          src={url}
          alt={attachment.original_name}
          className="w-full h-auto object-cover max-h-48"
        />
      </a>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-2 rounded-xl border px-2.5 py-1.5 text-xs font-bold transition ${
        isUserBubble
          ? 'border-white/30 bg-white/10 text-white hover:bg-white/20'
          : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'
      }`}
    >
      <AttachmentIcon kind={attachment.kind} size={14} />
      <span className="truncate max-w-[180px]">{attachment.original_name}</span>
      <span className={`text-[10px] font-normal ${isUserBubble ? 'opacity-70' : 'text-slate-500'}`}>
        {formatBytes(attachment.size_bytes)}
      </span>
    </a>
  );
}
